import {
    AccountInfo,
    AuthenticationResult,
    BrowserCacheLocation,
    CacheLookupPolicy,
    Configuration,
    ProtocolMode,
    PublicClientApplication,
    RedirectRequest,
} from '@azure/msal-browser';
import {MissingEntraIdCredentialsError} from './EntraIdErrors';

export const ENTRA_ID_SCOPES = ['openid', 'profile', 'https://api.csast.cz/default'];
const PREFERRED_DOMAINS = {
    TEST: 'csast.cz',
    PROD: 'csas.cz',
} as const;

export type EntraIdPreferredDomain = keyof typeof PREFERRED_DOMAINS;

export interface EntraIdConfig {
    clientId: string;
    authUrl: string;
    preferredDomain?: EntraIdPreferredDomain;
}

export class EntraIdClient {
    readonly pca: PublicClientApplication;
    readonly scopes = ENTRA_ID_SCOPES;
    private readonly preferredDomain: EntraIdPreferredDomain | undefined;
    private initializationPromise: Promise<PublicClientApplication> | undefined;
    private accessTokenExpiresAt: number | undefined;

    /**
     * Creates an Entra ID client bound to one immutable runtime configuration.
     * PCA construction is synchronous; MSAL startup and redirect processing remain in `initialize()`.
     *
     * @param config - Runtime Entra ID client, authority, and account-preference configuration.
     */
    constructor(config: EntraIdConfig) {
        this.preferredDomain = config.preferredDomain;
        this.pca = new PublicClientApplication(this.buildPcaConfiguration(config));
    }

    computeRedirectUrl(): string {
        return window.location.origin + window.location.pathname.replace(/\/$/, '') + '/';
    }

    /**
     * Initializes MSAL once, processes one redirect response, and restores a deterministic cached account.
     *
     * All concurrent callers receive the same promise. This protects the application-wide client from
     * component remounts or future callers independently starting MSAL initialization and redirect processing.
     * A rejected initialization is not cached, allowing a later explicit retry to start a fresh attempt.
     *
     * @returns The shared initialized MSAL public client application.
     * @throws Error when MSAL initialization, redirect processing, or cached-account cleanup fails.
     */
    initialize(): Promise<PublicClientApplication> {
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        const initializationPromise = this.initializePca();
        this.initializationPromise = initializationPromise;
        void initializationPromise.catch(() => {
            if (this.initializationPromise === initializationPromise) {
                this.initializationPromise = undefined;
            }
        });

        return initializationPromise;
    }

    /** Performs one MSAL startup, redirect-result processing, and cached-account restoration attempt. */
    private async initializePca(): Promise<PublicClientApplication> {
        await this.pca.initialize();
        const redirectResult = await this.pca.handleRedirectPromise();
        if (redirectResult?.account) {
            this.pca.setActiveAccount(redirectResult.account);
        } else {
            await this.selectPreferredAccountOrClearCache(this.preferredDomain);
        }
        return this.pca;
    }

    /**
     * Restores one deterministic cached account or clears ambiguous/inapplicable cached state.
     * A valid active account takes precedence; otherwise, selection follows the configured
     * preferred-domain policy. Clearing unresolved state allows the next interactive flow to
     * request explicit account selection instead of relying on MSAL cache order.
     *
     * @param preferredDomain - Optional environment used to select an applicable cached account.
     * @returns The selected account, or undefined after unresolved cached state has been cleared.
     */
    async selectPreferredAccountOrClearCache(
        preferredDomain?: EntraIdPreferredDomain,
    ): Promise<AccountInfo | undefined> {
        const accounts = this.pca.getAllAccounts();
        const account = this.findActiveOrPreferredAccount(accounts, preferredDomain);

        if (account) {
            this.pca.setActiveAccount(account);
            return account;
        }

        // No applicable account, or multiple applicable accounts, requires explicit account selection.
        await this.clearCachedAccounts();
        return undefined;
    }

    /**
     * Retrieves the current access token through deterministic silent account selection.
     *
     * @returns A usable access token.
     * @throws MissingEntraIdCredentialsError when no cached account or usable token is available.
     * @throws Error when MSAL token acquisition fails.
     */
    async getAccessToken(): Promise<string> {
        const authResult = await this.acquireTokenSilent();
        if (!authResult) {
            throw new MissingEntraIdCredentialsError('no-cached-account');
        }
        if (!authResult.accessToken?.trim()) {
            throw new MissingEntraIdCredentialsError('no-access-token');
        }
        const expiresAt = authResult.expiresOn?.getTime();
        this.accessTokenExpiresAt = expiresAt !== undefined && Number.isFinite(expiresAt) ? expiresAt : undefined;
        return authResult.accessToken;
    }

    /** The last access token's expiry, if MSAL supplied one; not an Entra session deadline. */
    getAccessTokenExpiresAt(): number | undefined {
        return this.accessTokenExpiresAt;
    }

    /**
     * Clears this application's MSAL cache and removes its active-account reference.
     *
     * @returns A promise that resolves after the cache and active account have been cleared.
     * @throws Error when MSAL cache clearing fails.
     */
    async clearCachedAccounts(): Promise<void> {
        await this.pca.clearCache();
        this.pca.setActiveAccount(null);
        this.accessTokenExpiresAt = undefined;
    }

    /**
     * Clears local MSAL state and ends the Entra ID browser session before returning to the application.
     * The account is captured first because it is required by the Entra end-session request after cache cleanup.
     *
     * @returns A promise that settles when MSAL starts or completes the logout redirect operation.
     */
    async logout(): Promise<void> {
        const account = this.pca.getActiveAccount() ?? undefined;
        await this.clearCachedAccounts();
        await this.pca.logoutRedirect({
            account,
            postLogoutRedirectUri: this.computeRedirectUrl(),
        });
    }

    /**
     * Acquires a token without interactive or hidden-iframe fallback for the active or preferred account.
     * Account resolution follows the same deterministic policy as interactive reauthentication.
     *
     * @returns The MSAL authentication result, or undefined when no unambiguous cached account exists.
     */
    async acquireTokenSilent(): Promise<AuthenticationResult | undefined> {
        const accounts = this.pca.getAllAccounts();
        if (!accounts.length) {
            return undefined;
        }

        const account = this.findActiveOrPreferredAccount(accounts, this.preferredDomain);
        if (!account) {
            return undefined;
        }
        this.pca.setActiveAccount(account);
        return this.pca.acquireTokenSilent({
            scopes: this.scopes,
            account,
            cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
        });
    }

    /**
     * Starts top-level interactive authentication using a deterministic account when possible.
     * The current active account is preferred for reauthentication; otherwise, the configured
     * domain selects one unambiguous cached account. If neither resolves an account, the request
     * explicitly asks MSAL to display the account picker instead of choosing implicitly.
     *
     * @returns A promise that settles when MSAL starts or completes the redirect operation.
     */
    async startInteractiveLogin(): Promise<void> {
        const accounts = this.pca.getAllAccounts();
        const account = this.findActiveOrPreferredAccount(accounts, this.preferredDomain);
        const request = this.buildAcquireTokenRequest(account);
        await this.pca.acquireTokenRedirect(request);
    }

    /**
     * Builds the MSAL configuration from runtime Entra settings. The configured client and
     * authority bind authentication to this deployment, while `knownAuthorities` restricts
     * authority trust to that configured endpoint. The redirect URI preserves the application's
     * current deployment path, local storage enables account reuse across reloads and tabs, and
     * OIDC protocol mode matches the authority's OpenID Connect endpoints.
     *
     * @param config - Runtime Entra ID settings used to configure the MSAL client.
     * @returns The complete configuration used to construct PublicClientApplication.
     */
    private buildPcaConfiguration(config: EntraIdConfig): Configuration {
        return {
            auth: {
                clientId: config.clientId,
                authority: config.authUrl,
                knownAuthorities: [config.authUrl],
                redirectUri: this.computeRedirectUrl(),
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
            },
            system: {
                protocolMode: ProtocolMode.OIDC,
            },
        };
    }

    /**
     * Builds an interactive token request for either a known account or explicit account selection.
     * A resolved account keeps reauthentication tied to that identity; otherwise, `select_account`
     * prevents MSAL from choosing an account implicitly.
     *
     * @param account - Account to reauthenticate, or undefined when the user must choose an account.
     * @returns The redirect request passed to MSAL.
     */
    private buildAcquireTokenRequest(account: AccountInfo | undefined): RedirectRequest {
        if (account) {
            return {
                scopes: this.scopes,
                account,
            };
        }

        return {
            scopes: this.scopes,
            prompt: 'select_account',
        };
    }

    /**
     * Resolves the active cached account or one unambiguous account for the preferred environment.
     * This centralizes account precedence so silent and interactive acquisition cannot diverge.
     *
     * @param accounts - Accounts currently available in the MSAL cache.
     * @param preferredDomain - Optional environment used when no valid active account exists.
     * @returns The active or sole preferred account, or undefined when selection is unresolved.
     */
    private findActiveOrPreferredAccount(
        accounts: AccountInfo[],
        preferredDomain?: EntraIdPreferredDomain,
    ): AccountInfo | undefined {
        return this.findActiveAccount(accounts)
            ?? this.selectPreferredAccount(accounts, preferredDomain);
    }

    /**
     * Returns the active account only when it is still present in MSAL's current account cache.
     * `getActiveAccount()` alone can return a stale reference after another tab or cache cleanup
     * removes that account. The lookup uses `homeAccountId`, MSAL's stable home-identity key,
     * instead of mutable usernames or JavaScript object identity.
     *
     * @param accounts - Accounts currently available in the MSAL cache.
     * @returns The current cached representation of the active account, or undefined when absent or stale.
     */
    private findActiveAccount(accounts: AccountInfo[]): AccountInfo | undefined {
        const activeAccount = this.pca.getActiveAccount();
        return activeAccount
            ? accounts.find(({homeAccountId}) => homeAccountId === activeAccount.homeAccountId)
            : undefined;
    }

    /**
     * Selects one unambiguous cached account for the configured environment.
     *
     * When a preferred domain is configured, only an exact, case-insensitive email-domain
     * match is applicable. Returning undefined for zero or multiple matches prevents callers
     * from relying on MSAL cache order. Without a preferred domain, a sole cached account is
     * unambiguous; multiple accounts still require explicit user selection.
     *
     * @param accounts - Accounts from which to select an environment-appropriate identity.
     * @param preferredDomain - Optional configured environment whose email domain is preferred.
     * @returns The sole applicable account, or undefined when selection is impossible or ambiguous.
     */
    selectPreferredAccount(
        accounts: AccountInfo[],
        preferredDomain?: EntraIdPreferredDomain,
    ): AccountInfo | undefined {
        if (!preferredDomain) {
            return accounts.length === 1 ? accounts[0] : undefined;
        }

        const resolvedPreferredDomain = PREFERRED_DOMAINS[preferredDomain];
        const preferredAccounts = accounts.filter(
            account => this.isAccountInPreferredDomain(account, resolvedPreferredDomain),
        );

        return preferredAccounts.length === 1 ? preferredAccounts[0] : undefined;
    }

    /**
     * Checks whether an account username belongs to the exact preferred email domain.
     *
     * @param account - Account whose username domain should be compared.
     * @param preferredDomain - Resolved email domain required for a match.
     * @returns True only when the account's normalized domain exactly matches the preferred domain.
     */
    private isAccountInPreferredDomain(account: AccountInfo, preferredDomain: string): boolean {
        return this.getDomainFromAccount(account) === preferredDomain.toLowerCase();
    }

    /**
     * Extracts the normalized email domain after the final `@` in an account username.
     *
     * @param account - Account whose username should be decomposed.
     * @returns The lowercase email domain, or undefined when the username has no valid domain.
     */
    private getDomainFromAccount(account: AccountInfo): string | undefined {
        const normalizedUsername = account.username.trim().toLowerCase();
        const domainSeparator = normalizedUsername.lastIndexOf('@');
        return domainSeparator > 0 && domainSeparator < normalizedUsername.length - 1
            ? normalizedUsername.substring(domainSeparator + 1)
            : undefined;
    }
}

let entraIdClient: EntraIdClient | undefined;

/**
 * Returns the application-wide Entra ID client, creating it on first configured access.
 * Runtime configuration is unavailable at module load, so singleton construction is lazy.
 *
 * @param config - Runtime configuration required when creating the singleton for the first time.
 * @returns The configured application-wide Entra ID client.
 * @throws Error when the singleton has not yet been created and no configuration is supplied.
 */
export function getEntraIdClient(config?: EntraIdConfig): EntraIdClient {
    if (!entraIdClient) {
        if (!config) {
            throw new Error('Entra ID configuration is required to create the client.');
        }
        entraIdClient = new EntraIdClient(config);
    }

    return entraIdClient;
}

